import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  AlertTriangle,
  Calendar,
  Clock,
  Phone,
  PhoneCall,
  RefreshCw,
  Copy,
  ChevronDown,
  ChevronUp,
  Building,
  ArrowRight,
  Sun,
  MessageSquare,
  Globe,
  ExternalLink,
  Users,
  Archive
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import QuickLogModal from '@/components/QuickLogModal';
import SetFollowUpModal from '@/components/SetFollowUpModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to format website URL
const formatWebsiteUrl = (url) => {
  if (!url) return null;
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
};

const getDisplayWebsite = (url) => {
  if (!url) return null;
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '').substring(0, 20) + (url.length > 30 ? '...' : '');
};

const LeadListItem = ({ lead, index, onCall, onFollowUp, onCopy, onNavigate, onQuickNote, onOpenNewTab }) => {
  return (
    <div 
      className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer group border border-transparent hover:border-primary/20"
      onClick={() => onNavigate(lead.id)}
      data-testid={`queue-lead-${lead.id}`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0 pr-4">
        {/* Row number */}
        <span className="text-xs text-muted-foreground font-mono w-6">#{index + 1}</span>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium truncate">{lead.full_name}</span>
            <Badge variant="outline" className={`text-xs ${lead.country_market === 'UK' ? 'badge-uk' : 'badge-usa'}`}>
              {lead.country_market}
            </Badge>
            {lead.local_time && (
              <Badge 
                variant="outline" 
                className={`text-xs ${lead.outside_call_hours ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : 'text-muted-foreground'}`}
              >
                <Clock className="w-3 h-3 mr-1" />
                {lead.local_time}
                {lead.outside_call_hours && (
                  <AlertTriangle className="w-3 h-3 ml-1" />
                )}
              </Badge>
            )}
            {lead.stale_lead && (
              <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-xs">
                Stale
              </Badge>
            )}
            {lead.do_not_call && (
              <Badge variant="destructive" className="text-xs">DNC</Badge>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1 truncate">
              <Building className="w-3 h-3 flex-shrink-0" />
              {lead.company_name}
            </span>
            <span className="font-mono text-xs">{lead.phone}</span>
            {lead.sector && lead.sector !== 'Other' && (
              <span className="text-xs">{lead.sector}</span>
            )}
            {lead.niche && lead.niche !== 'Other' && (
              <span className="text-xs text-primary/70">• {lead.niche}</span>
            )}
            {/* Call cadence indicator */}
            {lead.call_attempt_number > 0 && (
              <span className={`text-xs font-medium ${lead.call_attempt_number >= 5 ? 'text-amber-400' : ''}`}>
                Attempt {lead.call_attempt_number}/6
              </span>
            )}
            {lead.recommended_action && lead.call_attempt_number > 0 && (
              <span className="text-xs text-primary/70 italic">{lead.recommended_action}</span>
            )}
            {/* Website link */}
            {lead.website && (
              <a
                href={formatWebsiteUrl(lead.website)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 text-primary hover:underline text-xs"
              >
                <Globe className="w-3 h-3" />
                {getDisplayWebsite(lead.website)}
              </a>
            )}
          </div>
          {/* Appointment outcome badge */}
          {lead.stage === 'Appointment Scheduled' && lead.appointment_status && (
            <Badge variant="outline" className={`text-xs mt-1 ${
              lead.appointment_status === 'Attended' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' :
              lead.appointment_status === 'No show' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
              lead.appointment_status === 'Rescheduled' ? 'bg-blue-500/10 text-blue-400 border-blue-500/30' :
              lead.appointment_status === 'Cancelled' ? 'bg-gray-500/10 text-gray-400 border-gray-500/30' :
              'text-muted-foreground'
            }`}>
              Appt: {lead.appointment_status}
            </Badge>
          )}
        </div>
      </div>
      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
        <Button
          variant="default"
          size="sm"
          onClick={() => onCall(lead)}
          disabled={lead.do_not_call}
          title="Log Call"
        >
          <Phone className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onCopy(lead.phone)}
          className="border-border"
          title="Copy Phone"
        >
          <Copy className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onQuickNote(lead)}
          className="border-border"
          title="Quick Note"
        >
          <MessageSquare className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onFollowUp(lead)}
          className="border-border"
          title="Set Follow Up"
        >
          <Calendar className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onOpenNewTab(lead.id)}
          className="border-border"
          title="Open in New Tab"
        >
          <ExternalLink className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const QueueSection = ({ title, icon: Icon, leads, color, defaultOpen = true, onCall, onFollowUp, onCopy, onNavigate, onQuickNote, onOpenNewTab, emptyMessage }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const colorClasses = {
    red: 'text-red-400',
    blue: 'text-blue-400',
    amber: 'text-amber-400',
    purple: 'text-violet-400',
    emerald: 'text-emerald-400'
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="bg-card border-border">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-secondary/30 transition-colors rounded-t-lg">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Icon className={`w-5 h-5 ${colorClasses[color]}`} />
                {title}
                <Badge variant="secondary" className="ml-2">{leads.length}</Badge>
              </CardTitle>
              {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-2 pt-0">
            {leads.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-4">{emptyMessage}</p>
            ) : (
              leads.map((lead, index) => (
                <LeadListItem
                  key={lead.id}
                  lead={lead}
                  index={index}
                  onCall={onCall}
                  onFollowUp={onFollowUp}
                  onCopy={onCopy}
                  onNavigate={onNavigate}
                  onQuickNote={onQuickNote}
                  onOpenNewTab={onOpenNewTab}
                />
              ))
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

const TodayQueue = () => {
  const navigate = useNavigate();
  const { user, isSetter, isAdmin } = useAuth();
  const [queue, setQueue] = useState({
    overdue: [],
    due_today: [],
    appointments_today: [],
    no_touch_2_days: [],
    missing_follow_up: [],
    // Admin-only sections
    unassigned: [],
    stale_leads: [],
    no_touch_7_days: []
  });
  const [loading, setLoading] = useState(true);
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  
  // Quick note
  const [quickNoteOpen, setQuickNoteOpen] = useState(false);
  const [quickNoteText, setQuickNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    fetchQueue();
  }, []);

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/leads/today-queue`);
      setQueue(response.data);
    } catch (error) {
      toast.error('Failed to load today queue');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (phone) => {
    navigator.clipboard.writeText(phone);
    toast.success('Phone copied');
  };

  const handleCall = (lead) => {
    setSelectedLead(lead);
    setLogModalOpen(true);
  };

  const handleFollowUp = (lead) => {
    setSelectedLead(lead);
    setFollowUpModalOpen(true);
  };

  const handleNavigate = (leadId) => {
    // Save scroll position
    sessionStorage.setItem('today_queue_scroll', window.scrollY.toString());
    navigate(`/leads/${leadId}`);
  };

  const handleOpenNewTab = (leadId) => {
    window.open(`/leads/${leadId}`, '_blank');
  };

  const handleQuickNote = (lead) => {
    setSelectedLead(lead);
    setQuickNoteText('');
    setQuickNoteOpen(true);
  };

  const handleSaveQuickNote = async () => {
    if (!quickNoteText.trim() || !selectedLead) return;
    
    setSavingNote(true);
    try {
      await axios.post(`${API}/leads/${selectedLead.id}/quick-note`, {
        note: quickNoteText.trim()
      });
      toast.success('Note added');
      setQuickNoteOpen(false);
      fetchQueue();
    } catch (error) {
      toast.error('Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  // Restore scroll position
  useEffect(() => {
    const savedPosition = sessionStorage.getItem('today_queue_scroll');
    if (savedPosition) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedPosition));
        sessionStorage.removeItem('today_queue_scroll');
      }, 100);
    }
  }, []);

  // Calculate stats
  const totalLeads = queue.overdue.length + queue.due_today.length + queue.no_touch_2_days.length + queue.missing_follow_up.length;
  const urgentLeads = queue.overdue.length;
  const appointmentsToday = queue.appointments_today.length;

  // Count leads in call hours vs outside
  const allLeads = [...queue.overdue, ...queue.due_today, ...queue.no_touch_2_days, ...queue.missing_follow_up, ...queue.appointments_today];
  const inCallHours = allLeads.filter(l => !l.outside_call_hours);
  const outsideCallHours = allLeads.filter(l => l.outside_call_hours);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="today-queue-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Today's Queue</h1>
          <p className="text-muted-foreground mt-1">
            {isSetter ? `Your leads for today` : 'All leads requiring attention today'}
          </p>
        </div>
        <Button variant="outline" onClick={fetchQueue} className="border-border" data-testid="refresh-queue">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold">{totalLeads}</p>
            <p className="text-sm text-muted-foreground">Total to Contact</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border border-red-500/30">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-400">{urgentLeads}</p>
            <p className="text-sm text-muted-foreground">Overdue</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border border-emerald-500/30">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-emerald-400">{appointmentsToday}</p>
            <p className="text-sm text-muted-foreground">Appointments Today</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <Sun className="w-5 h-5 text-amber-400" />
              <p className="text-3xl font-bold text-amber-400">{inCallHours.length}</p>
            </div>
            <p className="text-sm text-muted-foreground">In Call Hours</p>
            {outsideCallHours.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                ({outsideCallHours.length} outside 9-6)
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Call Hours Warning */}
      {outsideCallHours.length > 0 && (
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div>
              <p className="font-medium text-amber-400">Outside Call Hours</p>
              <p className="text-sm text-muted-foreground">
                {outsideCallHours.length} leads are currently outside their local business hours (9am-6pm). 
                Look for the clock icon with warning indicator.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Queue Sections */}
      <div className="space-y-4">
        <QueueSection
          title="🔴 Overdue Follow-Ups"
          icon={AlertTriangle}
          color="red"
          leads={queue.overdue}
          defaultOpen={true}
          onCall={handleCall}
          onFollowUp={handleFollowUp}
          onCopy={handleCopy}
          onNavigate={handleNavigate}
          onQuickNote={handleQuickNote}
          onOpenNewTab={handleOpenNewTab}
          emptyMessage="No overdue follow-ups. Great job! 🎉"
        />

        <QueueSection
          title="📅 Due Today"
          icon={Calendar}
          color="blue"
          leads={queue.due_today}
          defaultOpen={true}
          onCall={handleCall}
          onFollowUp={handleFollowUp}
          onCopy={handleCopy}
          onNavigate={handleNavigate}
          onQuickNote={handleQuickNote}
          onOpenNewTab={handleOpenNewTab}
          emptyMessage="No leads scheduled for today"
        />

        <QueueSection
          title="📞 Appointments Today"
          icon={PhoneCall}
          color="emerald"
          leads={queue.appointments_today}
          defaultOpen={true}
          onCall={handleCall}
          onFollowUp={handleFollowUp}
          onCopy={handleCopy}
          onNavigate={handleNavigate}
          onQuickNote={handleQuickNote}
          onOpenNewTab={handleOpenNewTab}
          emptyMessage="No appointments scheduled for today"
        />

        <QueueSection
          title="⏰ No Touch in 2+ Days"
          icon={Clock}
          color="amber"
          leads={queue.no_touch_2_days}
          defaultOpen={queue.overdue.length === 0 && queue.due_today.length === 0}
          onCall={handleCall}
          onFollowUp={handleFollowUp}
          onCopy={handleCopy}
          onNavigate={handleNavigate}
          onQuickNote={handleQuickNote}
          onOpenNewTab={handleOpenNewTab}
          emptyMessage="All leads contacted recently"
        />

        <QueueSection
          title="⚠️ Missing Follow-Up Date"
          icon={AlertTriangle}
          color="purple"
          leads={queue.missing_follow_up}
          defaultOpen={false}
          onCall={handleCall}
          onFollowUp={handleFollowUp}
          onCopy={handleCopy}
          onNavigate={handleNavigate}
          onQuickNote={handleQuickNote}
          onOpenNewTab={handleOpenNewTab}
          emptyMessage="All contacted leads have follow-up dates set"
        />

        {/* Admin-only sections */}
        {isAdmin && queue.unassigned && queue.unassigned.length > 0 && (
          <QueueSection
            title="👤 Unassigned Leads"
            icon={Users}
            color="amber"
            leads={queue.unassigned}
            defaultOpen={false}
            onCall={handleCall}
            onFollowUp={handleFollowUp}
            onCopy={handleCopy}
            onNavigate={handleNavigate}
            onQuickNote={handleQuickNote}
            onOpenNewTab={handleOpenNewTab}
            emptyMessage="No unassigned leads"
          />
        )}

        {isAdmin && queue.stale_leads && queue.stale_leads.length > 0 && (
          <QueueSection
            title="📦 Stale Leads"
            icon={Archive}
            color="amber"
            leads={queue.stale_leads}
            defaultOpen={false}
            onCall={handleCall}
            onFollowUp={handleFollowUp}
            onCopy={handleCopy}
            onNavigate={handleNavigate}
            onQuickNote={handleQuickNote}
            onOpenNewTab={handleOpenNewTab}
            emptyMessage="No stale leads"
          />
        )}

        {isAdmin && queue.no_touch_7_days && queue.no_touch_7_days.length > 0 && (
          <QueueSection
            title="🔕 No Touch in 7+ Days"
            icon={Clock}
            color="red"
            leads={queue.no_touch_7_days}
            defaultOpen={false}
            onCall={handleCall}
            onFollowUp={handleFollowUp}
            onCopy={handleCopy}
            onNavigate={handleNavigate}
            onQuickNote={handleQuickNote}
            onOpenNewTab={handleOpenNewTab}
            emptyMessage="All leads contacted within 7 days"
          />
        )}
      </div>

      {/* Quick Note Dialog */}
      <Dialog open={quickNoteOpen} onOpenChange={setQuickNoteOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Quick Note for {selectedLead?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Enter your note..."
              value={quickNoteText}
              onChange={(e) => setQuickNoteText(e.target.value)}
              className="bg-secondary border-border min-h-[100px]"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickNoteOpen(false)} className="border-border">
              Cancel
            </Button>
            <Button onClick={handleSaveQuickNote} disabled={savingNote || !quickNoteText.trim()}>
              {savingNote ? 'Saving...' : 'Save Note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modals */}
      <QuickLogModal
        open={logModalOpen}
        onOpenChange={setLogModalOpen}
        leadId={selectedLead?.id}
        lead={selectedLead}
        onSuccess={fetchQueue}
      />

      <SetFollowUpModal
        open={followUpModalOpen}
        onOpenChange={setFollowUpModalOpen}
        lead={selectedLead}
        onSuccess={fetchQueue}
      />
    </div>
  );
};

export default TodayQueue;
